import { FC, HTMLAttributes } from 'react';
import sanitizeHtml from 'sanitize-html';

const QUOTE_OPTIONS: sanitizeHtml.IOptions = {
    allowedTags: ['p', 'strong', 'em', 's', 'u', 'h1', 'h2', 'ul', 'ol', 'li'],
};

const COLOR = [/^#[0-9a-f]{3,8}$/i, /^rgba?\([\d\s.,%]+\)$/i, /^[a-z]+$/i];
const SIZE = [/^[\d.]+(px|pt|em|rem|%)?$/i, /^[a-z-]+$/i];
const KEYWORDS = [/^[a-z\s-]+$/i, /^\d{3}$/];

// Text blocks are editable by page-limited employees: formatting only — no script, handlers,
// style sheets, frames, positioning CSS or link schemes other than http(s)/mailto.
const TEXT_BLOCK_OPTIONS: sanitizeHtml.IOptions = {
    allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img', 'font', 'center', 'ins', 'del'],
    allowedAttributes: {
        '*': ['style', 'align', 'title'],
        a: ['href', 'target', 'rel'],
        img: ['src', 'alt', 'width', 'height'],
        table: ['border', 'cellpadding', 'cellspacing', 'width', 'bgcolor'],
        td: ['colspan', 'rowspan', 'width', 'bgcolor', 'valign'],
        th: ['colspan', 'rowspan', 'width', 'bgcolor', 'valign'],
        font: ['color', 'size', 'face'],
        ins: ['data-operation-index'],
        del: ['data-operation-index'],
    },
    allowedClasses: { '*': ['ql-*'] },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesByTag: { img: ['http', 'https'] },
    allowedStyles: {
        '*': {
            color: COLOR,
            'background-color': COLOR,
            'font-family': [/^[\w\s,"'-]+$/],
            'font-size': SIZE,
            'font-weight': KEYWORDS,
            'font-style': KEYWORDS,
            'text-align': KEYWORDS,
            'text-decoration': KEYWORDS,
        },
    },
};

export const sanitizeTextBlockHtml = (html: string): string => sanitizeHtml(html, TEXT_BLOCK_OPTIONS);

interface SafeHTMLRenderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'dangerouslySetInnerHTML'> {
    html?: string | null;
    /** a text block or translation; without it the landing-page quote's narrow tag list applies */
    textBlock?: boolean;
}

export const SafeHTMLRender: FC<SafeHTMLRenderProps> = ({ html, textBlock, ...divProps }) => {
    if (html == null) return null;
    return (
        <div
            {...divProps}
            dangerouslySetInnerHTML={{
                __html: textBlock ? sanitizeTextBlockHtml(html) : sanitizeHtml(html, QUOTE_OPTIONS),
            }}
        />
    );
};
